import { useEffect } from 'react';
import { isSeatLimitReached } from '@gonfalon/billing/subscriptions';
import { hasLegacyPlan as _hasLegacyPlan } from '@gonfalon/constants/constants';
import {
  enableEnterpriseSeatExpansions as enableEnterpriseSeatExpansionsContext,
  enableNewInviteMembersPermissionsLogic,
  isEmailVerificationRequired,
  isSeatLimitEnforced as _isSeatLimitEnforced,
  isSSOEnabled,
  isTeamsEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import type { List } from '@gonfalon/immutable-shim/immutableShim';
import { hasAdminRights } from '@gonfalon/members/hasAdminRights';
import { hasCreateMemberRights } from '@gonfalon/members/hasCreateMemberRights';

import {
  fetchLegacySubscription as fetchLegacySubscriptionAction,
  fetchSubscription as fetchSubscriptionAction,
  fetchSubscriptionUsage as fetchSubscriptionUsageAction,
} from '#actions/billing';
import { fetchProfile as fetchProfileAction } from '#actions/profile';
import { fetchSamlConfig as fetchSamlConfigAction } from '#actions/sso';
import { useDispatch } from '#hooks/useDispatch';
import { legacySubscriptionSelector, subscriptionSelector, subscriptionUsageSelector } from '#reducers/billing';
import { bulkInvitedMembersSelector } from '#reducers/members';
import { profileSelector } from '#reducers/profile';
import { samlConfigSelector } from '#reducers/samlConfig';
import type { Member } from '#utils/members/records/Member';
import type { RequestState } from '#utils/reduxUtils';
import { ready } from '#utils/reduxUtils';

import { useSelector } from './useSelector';

export const READ_ONLY_DISABLED_REASON =
  'This account has limited access and cannot invite members. Please select a plan.';
export const HAS_SSO_DISABLED_REASON = 'SSO accounts cannot invite members';

const useRedux = () => {
  const legacySubscription = useSelector(legacySubscriptionSelector);
  const subscriptionUsage = useSelector(subscriptionUsageSelector);
  const subscription = useSelector(subscriptionSelector);
  const samlConfig = useSelector(samlConfigSelector);
  const profile = useSelector(profileSelector);
  const profileEntity = profile?.get('entity');
  const enableTeams = isTeamsEnabled();
  const emailVerificationRequired = isEmailVerificationRequired();
  const enableEnterpriseSeatExpansions = enableEnterpriseSeatExpansionsContext();
  const bulkInvitedMembers = useSelector(bulkInvitedMembersSelector);
  const invitedMembers = bulkInvitedMembers?.get('members');
  const dispatch = useDispatch();
  const fetchSubscriptionUsage = () => {
    void dispatch(fetchSubscriptionUsageAction());
  };
  const fetchLegacySubscription = () => {
    void dispatch(fetchLegacySubscriptionAction());
  };

  const fetchSubscription = () => {
    void dispatch(fetchSubscriptionAction());
  };

  const fetchSamlConfig = () => dispatch(fetchSamlConfigAction());

  const fetchProfile = () => {
    void dispatch(fetchProfileAction());
  };

  let resources: Array<RequestState | undefined> = [];
  if (isSSOEnabled()) {
    resources.push(samlConfig.request);
  }
  if (_isSeatLimitEnforced()) {
    if (_hasLegacyPlan()) {
      resources.push(legacySubscription);
    } else {
      resources = resources.concat([subscription.fetchRequest, subscriptionUsage.fetchRequest]);
    }
  }

  return {
    isReady: ready(...resources),
    isSeatLimitEnforced: _isSeatLimitEnforced(),
    hasLegacyPlan: !!_hasLegacyPlan(),
    profileEntity,
    samlConfig,
    legacySubscription,
    subscription,
    subscriptionUsage,
    enableTeams,
    invitedMembers,
    emailVerificationRequired,
    fetchSubscriptionUsage,
    fetchLegacySubscription,
    fetchSubscription,
    fetchSamlConfig,
    fetchProfile,
    enableEnterpriseSeatExpansions,
  };
};

export function useInviteMembersOption(): {
  canInviteMembers: boolean;
  invitedMembers?: List<Member>;
  isReadOnly: boolean;
  renderRequestSeats: boolean;
  shouldDisableButton: boolean;
  disabledReasons: string[];
  hasSSO: boolean;
} {
  const {
    isSeatLimitEnforced,
    hasLegacyPlan,
    isReady,
    samlConfig,
    legacySubscription,
    subscription,
    subscriptionUsage,
    invitedMembers,
    enableTeams,
    profileEntity,
    emailVerificationRequired,
    fetchSubscriptionUsage,
    fetchLegacySubscription,
    fetchSubscription,
    fetchSamlConfig,
    fetchProfile,
    enableEnterpriseSeatExpansions,
  } = useRedux();

  useEffect(() => {
    if (!isReady) {
      void fetchSamlConfig();
      fetchProfile();
      if (isSeatLimitEnforced) {
        if (hasLegacyPlan) {
          fetchLegacySubscription();
        } else {
          fetchSubscription();
          fetchSubscriptionUsage();
        }
      }
    }
  }, []);

  const hasSSO = !!samlConfig?.entity?.isEnabled();

  const atSeatLimit = isSeatLimitReached(
    hasLegacyPlan ? legacySubscription?.get('entity').toJS() : subscription.entity.toJS(),
    subscriptionUsage.entity.toJS(),
  );

  const isEmailVerificationPending = emailVerificationRequired && !profileEntity?.isVerified();

  const isReadOnly =
    subscription?.entity?.isReadOnly() || (hasLegacyPlan && legacySubscription?.get('entity')?.isReadOnly());

  const isEnterprise = subscription?.entity?.isEnterprise();

  const renderRequestSeats = isEnterprise && !!atSeatLimit && enableEnterpriseSeatExpansions;
  /*
   Users that can invite members:
   - Non SSO accounts
   - Verified profiles with admin rights
   - Accounts that have teams enabled
   ** For old plans only :
   - Accounts that have not reached seat limit
  */

  const profilePlainJs = profileEntity.toJS();
  const profileCanInviteMembers = enableNewInviteMembersPermissionsLogic()
    ? hasCreateMemberRights(profilePlainJs, profilePlainJs)
    : hasAdminRights(profilePlainJs);

  const requiredConditionsForInviting = [
    profileCanInviteMembers,
    !hasSSO,
    !isEmailVerificationPending,
    !subscription?.entity.is2021Plan() ? !atSeatLimit : true,
    enableTeams,
  ];

  const shouldDisableButton = hasSSO || isReadOnly;
  const disabledReasons = [
    ...(hasSSO ? [HAS_SSO_DISABLED_REASON] : []),
    ...(isReadOnly ? [READ_ONLY_DISABLED_REASON] : []),
  ];

  return {
    renderRequestSeats,
    invitedMembers,
    shouldDisableButton,
    canInviteMembers: requiredConditionsForInviting.every((c) => !!c),
    isReadOnly,
    disabledReasons,
    hasSSO,
  };
}
